import React, { useEffect, useRef } from "react";

import { AdvertImageNotAvailable, Usage } from "@at/design-system-patterns";

import { GallerySliderImage } from "./gallery-slider-image";
import { StyledGallerySliderContainer } from "./gallery-slider.styled";

import {
  comScorePageViewTrack,
  getGalleryImageContext,
  ImageGalleryState,
} from "../../../utils/tracking/tracking-data-utils";
import { useTracking } from "../../../utils/tracking/use-tracking";
import {
  GalleryImage,
  ImageOnClickHandler,
} from "../gallery-image/gallery-image";
import { GalleryAsset } from "../gallery.utils";
import { onSliderUpdateHandler, Slider } from "../slider/slider";

import { GallerySliderImage as StyledGallerySliderImage } from "../slider/slider.styled";
import { FpaGridViewMoreImages } from "../view-more-images/view-more-images";

interface GallerySliderProps {
  images: GalleryAsset[];
  isLeasingPDPPage?: boolean;
  hasSpin: boolean;
  hasVideo: boolean;
  showViewMore: boolean;
  onImageClick: ImageOnClickHandler;
}

export function GallerySlider({
  images,
  isLeasingPDPPage,
  hasSpin,
  hasVideo,
  showViewMore,
  onImageClick,
}: GallerySliderProps) {
  const firedComponentViewedEvents = useRef<number[]>([]);
  const { trackComponentViewedEvent } = useTracking();

  const onSliderUpdate: onSliderUpdateHandler = (index) => {
    comScorePageViewTrack();

    if (
      !firedComponentViewedEvents.current.includes(index) &&
      images.length > 0
    ) {
      firedComponentViewedEvents.current.push(index);
      const src = images[index].image.url;
      const imageId = src?.match(/([\dA-Za-z]+)\.jpg/)?.[1] || src;

      trackComponentViewedEvent({
        extraEntities: [
          getGalleryImageContext(imageId, ImageGalleryState.in_page, index),
        ],
      });
    }
  };

  useEffect(() => {
    onSliderUpdate(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledGallerySliderContainer
      data-testid={"gallery-slider-image-container"}
      isLeasingPage={isLeasingPDPPage}
    >
      {images.length > 0 ? (
        <Slider initialSlide={0} onSliderUpdate={onSliderUpdate}>
          {images.map((slide, index) => {
            const src = slide.image.url;
            const alt = slide.image.alt;

            return (
              <StyledGallerySliderImage
                key={slide ? `${slide?.type}-${slide?.image?.url}` : index}
              >
                <GalleryImage
                  {...{
                    src,
                    index,
                    dataTestId: `open-carousel-${index + 1}`,
                    onClick: onImageClick,
                  }}
                >
                  <GallerySliderImage
                    src={src}
                    alt={alt}
                    shouldLazyLoad={index !== 0}
                  />
                </GalleryImage>
              </StyledGallerySliderImage>
            );
          })}
        </Slider>
      ) : (
        <AdvertImageNotAvailable usage={Usage.Main} />
      )}
      {showViewMore && (
        <FpaGridViewMoreImages
          showSlide={() => {}}
          viewSpin={!!hasSpin}
          viewVideo={!!hasVideo}
          viewImage={images.length > 0}
        />
      )}
    </StyledGallerySliderContainer>
  );
}
